<template>
    <div class="flex gap-7">
        <ConfirmDialog></ConfirmDialog>
        <LibyanOrnament vertical />
        <div class="foal-form flex flex-column gap-4 justify-content-between w-full">
            <h2 class="page-header">{{$t('message.horseCreate.foalHeader')}}</h2>
            <div class="foal-covering-certificate flex gap-5">
                <div class="flex flex-column w-5 gap-3 foal-covering-certificate-inner">
                    <CustomAutocompleteCoveringCertificate name="coveringCertificateAutocomplete"
                                                           :label="$t('message.horseCreate.coveringCertificate')" searchParam="label"
                                                           v-model="horse.selectedCoveringCertificate" noPlaceholder
                                                           :key="computedSelectedCoveringCertificateId"
                                                           :required="computedRequired"
                                                           :customErrorMessage="getRequiredMessage(horse.coveringCertificate)"
                                                           :disabled="disableCertificateEdition"/>
                </div>
                <div class="dummy flex w-5"></div>
            </div>
            <div class="separator"/>
            <FoalMainData v-model="horse" :systemRole="systemRole" :showErrorMessages="showErrorMessages"
                          @colorValidationResult="handleColorValidation" :triggerColorValidation="shouldValidateColor"
                          @gestationPeriodValidationResult="handleGestationPeriodValidation"/>
            <div v-if="displayMarkingData" class="separator"/>
            <FoalMarkingData v-if="displayMarkingData" v-model="horse" :systemRole="systemRole"
                             :showErrorMessages="showErrorMessages" @importPhoto="importPhoto"
                             :addFoalView="true"/>
            <div class="separator"/>
            <StudbookEntryStatus :horse="horse"/>
            <CustomInputTextArea name="notes" :label="$t('message.horseEdit.main.notes')" v-model="horse.notes" class="w-full"/>
            <div v-if="registrationBlockedDueToColorMismatch" class="flex justify-content-center align-items-center gap-2 mt-3">
                <i style="color: red;" class="pi pi-exclamation-triangle attention-icon" />
                <span style="color: red;">{{ $t('message.horseCreate.registrationBlockedDueToColorMismatch') }}</span>
            </div>
            <div v-if="registrationBlockedDueToDates" class="flex justify-content-center align-items-center gap-2 mt-3">
                <i style="color: red;" class="pi pi-exclamation-triangle attention-icon" />
                <span style="color: red;">{{ $t('message.horseCreate.registrationBlockedDueToDates') }}</span>
            </div>
            <div class="flex justify-content-center mt-3 gap-3">
                <CustomButton :label="$t('message.horseCreate.saveHorse')"
                              @click="saveHorse"
                              :disabled="disableSubmitting || (!isAdmin && (registrationBlockedDueToDates || registrationBlockedDueToColorMismatch))"/>
                <CustomButton :label="this.horse.horseStatus === horseStatus.LABORATORY_DATA_ENTERED
                                  ? $t('message.horseCreate.registerInStudbook')
                                  : $t('message.horseCreate.submitHorse')"
                              secondary @click="submitHorse"
                              :disabled="disableSubmitting || (!isAdmin && (registrationBlockedDueToDates || registrationBlockedDueToColorMismatch))"/>
                <CustomButton v-if="displayRejectButton" :label="$t('message.horseCreate.rejectHorse')"
                              @click="rejectHorse"
                              :disabled="disableSubmitting"/>
            </div>
        </div>
    </div>
</template>

<script>
import Button from "primevue/button";
import Toolbar from "primevue/toolbar";
import { Form } from "vee-validate";
import ConfirmDialog from "primevue/confirmdialog";
import CustomInputText from "../../../components/form/CustomInputText.vue";
import CustomMultiSelectHorseColor from "@/components/form/CustomMultiSelectHorseColor.vue";
import CustomSelectOneHorseBreed from "@/components/form/CustomSelectOneHorseBreed.vue";
import CustomCalendar from "../../../components/form/CustomCalendar.vue";
import CustomCheckbox from "../../../components/form/CustomCheckbox.vue";
import {
    getFoal,
    rejectFoal,
    saveFoal,
    setHorseAvatar,
    submitFoal,
} from "@/swagger/vue-api-client";
import CustomSelectOneHorseSex from "@/components/form/CustomSelectOneHorseSex.vue";
import CustomSelectOneCountry from "@/components/form/CustomSelectOneCountry.vue";
import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
import CustomAutocompleteUser from "@/components/form/CustomAutocompleteUser.vue";
import OwnerAddDialog from "@/views/owner/OwnerAddDialog.vue";
import CustomSelectOneHorseColor from "@/components/form/CustomSelectOneHorseColor.vue";
import HorseEditMainMainData
    from "@/views/horse/horseEdit/main/components/HorseEditMainMainData.vue";
import HorseEditMainStudbooks
    from "@/views/horse/horseEdit/main/components/HorseEditMainStudbooks.vue";
import HorseEditMainOtherData
    from "@/views/horse/horseEdit/main/components/HorseEditMainOtherData.vue";
import HorseEditMainPedigree
    from "@/views/horse/horseEdit/main/components/HorseEditMainPedigree.vue";
import HorseEditOwners from "@/views/horse/horseEdit/main/components/HorseEditOwners.vue";
import HorseEditMainDates from "@/views/horse/horseEdit/main/components/HorseEditMainDates.vue";
import CustomButton from "@/components/form/CustomButton.vue";
import FoalMainData from "@/views/horse/foal/components/FoalMainData.vue";
import StudbookEntryStatus from "@/views/horse/horseEdit/main/components/StudbookEntryStatus.vue";
import CustomInputTextArea from "@/components/form/CustomInputTextArea.vue";
import LibyanOrnament from "@/components/LibyanOrnament.vue";
import FoalMarkingData from "./components/FoalMarkingData.vue";
import { ValidateUtils } from "@/utils/ValidateUtils";
import {SystemRole} from "@/utils/SystemRole";
import {HorseStatus} from "@/utils/HorseStatus";
import CustomAutocompleteCoveringCertificate
    from "@/components/form/CustomAutocompleteCoveringCertificate.vue";

export default {
    name: "FoalFormView",
    components: {
        CustomInputTextArea,
        StudbookEntryStatus,
        FoalMainData,
        CustomButton,
        HorseEditMainDates,
        HorseEditOwners,
        HorseEditMainPedigree,
        HorseEditMainOtherData,
        HorseEditMainStudbooks,
        HorseEditMainMainData,
        CustomSelectOneHorseColor,
        OwnerAddDialog,
        CustomAutocompleteUser,
        CustomInputNumber,
        CustomSelectOneCountry,
        CustomSelectOneHorseSex,
        CustomSelectOneHorseBreed,
        CustomMultiSelectHorseColor,
        Button,
        Toolbar,
        Form,
        CustomInputText,
        CustomCalendar,
        CustomCheckbox,
        LibyanOrnament,
        FoalMarkingData,
        CustomAutocompleteCoveringCertificate,
        ConfirmDialog,
    },
    data() {
        return {
            showErrorMessages: false,
            showGestationPeriodWarning: false,
            disableSubmitting: false,
            horse: {
                id: null,
                name: null,
                localName: null,
                sex: null,
                breed: null,
                colorId: null,
                dateOfBirth: null,
                birthPlace: null,
                dateOfDeclaration: null,
                countryOfBirthId: null,
                countryOfImportId: null,
                dateOfImport: null,
                ueln: null,
                horseStatus: null,
                transponderCode: null,
                controlledBy: null,
                dateOfControl: null,
                dnaSampleExists: false,
                studbookRegistryId: null,
                bookNumber: null,
                bookPage: null,
                bookAppendixNumber: null,
                dateOfDeath: null,
                notes: null,
                breeders: [],
                owners: [],
                father: null,
                mother: null,
                selectedCoveringCertificate: null,
                sireFullNameWithCountryCode: null,
                damFullNameWithCountryCode: null,
                sireColorName: null,
                damColorName: null,
                lastCoveringDate: null,
                laboratoryTest: [],
                horseClassification: null,
                strain: null,
                // Marking data properties
                starSize: [],
                starPosition: [],
                starRelToWhorl: [],
                faceWhorl: [],
                stripe: [],
                snipAndFleshMark: [],
                whiteMarksLF: [],
                spotOnCoronetLF: [],
                hoofLF: [],
                whiteMarksRF: [],
                spotOnCoronetRF: [],
                hoofRF: [],
                whiteMarksLH: [],
                spotOnCoronetLH: [],
                hoofLH: [],
                whiteMarksRH: [],
                spotOnCoronetRH: [],
                hoofRH: [],
                underMainWhorls: [],
                underNeckWhorls: [],
                marks: [],
                markPositions: [],
                // Other values for marking data
                starSizeOther: "",
                starPositionOther: "",
                starRelToWhorlOther: "",
                faceWhorlOther: "",
                stripeOther: "",
                snipAndFleshMarkOther: "",
                whiteMarksLFOther: "",
                spotOnCoronetLFOther: "",
                hoofLFOther: "",
                whiteMarksRFOther: "",
                spotOnCoronetRFOther: "",
                hoofRFOther: "",
                whiteMarksLHOther: "",
                spotOnCoronetLHOther: "",
                hoofLHOther: "",
                whiteMarksRHOther: "",
                spotOnCoronetRHOther: "",
                hoofRHOther: "",
                underMainWhorlsOther: "",
                underNeckWhorlsOther: "",
                marksOther: "",
                markPositionsOther: "",
            },
            dialogVisible: false,
            sameAsBreeders: false,
            loaded: false,
            systemRole: SystemRole.getUserRole(),
            horseStatus: HorseStatus,
            importedPhoto: null,
            shouldValidateColor: false,
            registrationBlockedDueToDates: false,
            registrationBlockedDueToColorMismatch: false,
        };
    },
    beforeMount() {
        this.loadData();
        this.loaded = true;
    },
    methods: {
        loadData() {
            if (this.$route.params.id) {
                const allowedRolesFirstStep = [
                    SystemRole.FIRST_LEVEL_EDITOR,
                    SystemRole.SECOND_LEVEL_EDITOR,
                    SystemRole.ADMIN,
                ];
                const allowedRolesMarkingData = [
                    SystemRole.SECOND_LEVEL_EDITOR,
                    SystemRole.THIRD_LEVEL_EDITOR,
                    SystemRole.ADMIN,
                ];
                const allowedRolesThirdStep = [
                    SystemRole.THIRD_LEVEL_EDITOR,
                    SystemRole.ADMIN,
                ];

                getFoal({ id: this.$route.params.id }).then((response) => {
                    this.horse = response.data;
                    switch (this.horse.horseStatus) {
                        case HorseStatus.NEW_FOAL:
                            if (!allowedRolesFirstStep.includes(this.systemRole)) {
                                this.$router.push({name: "forbidden"});
                            }
                            break;
                        case HorseStatus.WAITING_FOR_MARKING_DATA:
                            if (!allowedRolesMarkingData.includes(this.systemRole)) {
                                this.$router.push({name: "forbidden"});
                            }
                            break;
                        case HorseStatus.LABORATORY_DATA_ENTERED:
                            if (!allowedRolesThirdStep.includes(this.systemRole)) {
                                this.$router.push({name: "forbidden"});
                            }
                            break;
                        default:
                            this.$router.push({name: "forbidden"});
                    }
                });
            }
            this.disableSubmitting = false;
        },
        validateData() {
            let isCorrect = true;
            const allowedRolesFirstStep = [
                SystemRole.FIRST_LEVEL_EDITOR,
                SystemRole.SECOND_LEVEL_EDITOR,
                SystemRole.ADMIN,
            ];
            const allowedRolesMarkingData = [
                SystemRole.SECOND_LEVEL_EDITOR,
                SystemRole.THIRD_LEVEL_EDITOR,
                SystemRole.ADMIN,
            ];
            if (allowedRolesFirstStep.includes(this.systemRole)) {
                isCorrect = ValidateUtils.validateNotEmpty(this.horse, ["name", "localName", "sex", "colorId", "dateOfBirth", "sireFullNameWithCountryCode", "damFullNameWithCountryCode"]) && isCorrect;
                const filteredBreeders = this.horse.breeders.filter((el) => el !== null);
                const filteredOwners = this.horse.owners.filter((el) => el !== null);
                isCorrect = filteredBreeders !== 0 && isCorrect;
                isCorrect = filteredOwners !== 0 && isCorrect;
            }
            if (allowedRolesMarkingData.includes(this.systemRole) && this.horse?.horseStatus === HorseStatus.WAITING_FOR_MARKING_DATA) {
                isCorrect = ValidateUtils.validateNotEmpty(this.horse, ["dateOfControl", "transponderCode"]) && isCorrect;
            }
            this.showErrorMessages = !isCorrect;
            if (this.showErrorMessages) {
                this.$toast.add({
                    severity: "error",
                    summary: this.$t("message.other.error"),
                    detail: this.$t("message.other.formError"),
                    life: 5000,
                });
            }
            return isCorrect;
        },
        setBreed() {
            if (localStorage.getItem("horseBreed") === "ARABIAN") {
                this.horse.breed = "ARABIAN";
            }
            if (localStorage.getItem("horseBreed") === "THOROUGHBRED") {
                this.horse.breed = "THOROUGHBRED";
            }
        },
        saveHorse() {
            this.disableSubmitting = true;
            this.setBreed();
            saveFoal({ body: this.horse }).then((res) => {
                this.$toast.add({
                    severity: "success",
                    summary: this.$t("message.other.success"),
                    detail: this.$t("message.other.dataSaved"),
                    life: 3000,
                });
                if (this.importedPhoto) {
                    this.axios.post(
                        `${process.env.VUE_APP_API_URL}/files/photos/${res.data}`,
                        this.importedPhoto,
                        {
                            headers: {
                                "Content-Type": "multipart/form-data",
                            },
                        },
                    ).then((response) => {
                        setHorseAvatar({
                            horseId: res.data,
                            photoId: response.data[0],
                        });
                    });
                }
                this.horse.id = res.data;
                this.$router.push({path: "/horse/foal/" + this.horse.id});
                this.loadData();
            }).catch((err) => {
                this.notifyError(err);
                this.disableSubmitting = false;
            });
        },
        submitHorse() {
            this.setBreed();
            if (this.validateData()) {
                submitFoal({ body: this.horse }).then((res) => {
                    this.$toast.add({
                        severity: "success",
                        summary: this.$t("message.other.success"),
                        detail: this.$t("message.other.dataSaved"),
                        life: 3000,
                    });
                    if (this.importedPhoto) {
                        this.axios.post(
                            `${process.env.VUE_APP_API_URL}/files/photos/${res.data}`,
                            this.importedPhoto,
                            {
                                headers: {
                                    "Content-Type": "multipart/form-data",
                                },
                            },
                        ).then((response) => {
                            setHorseAvatar({
                                horseId: res.data,
                                photoId: response.data[0],
                            });
                        });
                    }
                    this.horse.id = res.data;
                    this.$router.push({path: "/pending/list"});
                    this.disableSubmitting = false;
                }).catch((err) => {
                    this.notifyError(err);
                    this.disableSubmitting = false;
                });
            }
        },
        rejectHorse() {
            rejectFoal({ id: this.horse.id }).then(() => {
                this.$toast.add({
                    severity: "success",
                    summary: this.$t("message.other.success"),
                    detail: this.$t("message.other.dataSaved"),
                    life: 3000,
                });
                this.disableSubmitting = false;
                this.$router.push({path: "/"});
            });
        },
        notifyError(error) {
            if (error.response?.data) {
                for (const [key, value] of Object.entries(error.response.data)) {
                    if (key !== "path" && key !== "status" && key !== "timestamp") {
                        this.$toast.add({
                            severity: "error",
                            summary: this.$t("message.other.error"),
                            detail: value,
                            life: 5000,
                        });
                    }
                }
            } else {
                this.$toast.add({
                    severity: "error",
                    summary: this.$t("message.other.error"),
                    detail: this.$t("unknownError"),
                    life: 5000,
                });
            }
        },
        getRequiredMessage(value) {
            if (this.showErrorMessages && (value === null || value === "")) {
                return this.$t("message.validations.required");
            }
            return "";
        },
        importPhoto(value) {
            this.importedPhoto = value;
        },
        addBreeder() { this.horse.breeders.push({}); },
        removeBreeder(index) { this.horse.breeders.splice(index, 1); },
        addOwner() { this.horse.owners.push({}); },
        removeOwner(index) { this.horse.owners.splice(index, 1); },
        copyBreedersToOwners() {
            if (this.sameAsBreeders) {
                this.horse.owners = [...this.horse.breeders];
            }
        },
        handleColorValidation(isValid) {
            if (!isValid) {
                this.$confirm.require({
                    message: "Cannot register the foal due to color inconsistency.",
                    header: "Color inconsistency",
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "OK",
                    rejectLabel: null,
                    accept: () => {},
                    reject: () => {},
                });
            }
            this.registrationBlockedDueToColorMismatch = !isValid;
        },
        handleGestationPeriodValidation() {
            this.validateDateOfBirth();
        },
        parseDate(dateStr) {
            if (!dateStr || typeof dateStr !== "string") return null;

            const [day, month, year] = dateStr.split("-");
            if (!day || !month || !year) return null;

            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        },
        validateDateOfBirth() {
            const birthDate = this.horse.dateOfBirth;
            const dobDate = this.parseDate(birthDate);
            const coveringDate = this.parseDate(this.horse.lastCoveringDate);

            if (!dobDate || !coveringDate) {
                return;
            }
            // Calculate difference in days
            const diffTime = dobDate - coveringDate;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays > 400) {
                this.registrationBlockedDueToDates = true;
                this.showAlert("Cannot register the foal due to difference between covering date and date of birth more than 400 days.", "Registration blocked");
            } else if (diffDays < 300) {
                this.registrationBlockedDueToDates = true;
                this.showAlert("Cannot register the foal due to difference between covering date and date of birth less than 300 days.", "Registration blocked");
            } else if (diffDays >= 320 && diffDays <= 365) {
                this.registrationBlockedDueToDates = false;
            } else if ((diffDays >= 300 && diffDays < 320) || (diffDays > 365 && diffDays <= 400)) {
                this.registrationBlockedDueToDates = false;
                this.showAlert("Please confirm the date of birth.", "Warning");
            } else {
                this.registrationBlockedDueToDates = false;
            }
        },
        showAlert(message, header) {
            this.$confirm.require({
                message,
                header,
                icon: "pi pi-exclamation-triangle",
                acceptLabel: "OK",
                rejectLabel: null,
                accept: () => {},
                reject: () => {},
            });
        },
    },
    computed: {
        isAdmin() {
            return this.systemRole === SystemRole.ADMIN;
        },
        disableCertificateEdition() {
            if (this.horse.horseStatus == null || this.horse.horseStatus === HorseStatus.NEW_FOAL) {
                return false;
            }
            if (this.horse.horseStatus === HorseStatus.WAITING_FOR_MARKING_DATA && this.horse.laboratoryTest.length > 1) {
                const testResult = this.horse.laboratoryTest[this.horse.laboratoryTest.length - 2].laboratoryTestResult;
                return !(testResult === "PARENTAGE_NOT_VERIFIED" || testResult === "NEEDS_RECOLLECTION");
            }
            return true;
        },
        displayMarkingData() {
            const allowedRoles = [
                SystemRole.SECOND_LEVEL_EDITOR,
                SystemRole.THIRD_LEVEL_EDITOR,
                SystemRole.ADMIN,
            ];

            const allowedStatuses = [
                HorseStatus.WAITING_FOR_MARKING_DATA,
                HorseStatus.LABORATORY_DATA_ENTERED,
                HorseStatus.REJECTED,
            ];

            return allowedRoles.includes(this.systemRole) && allowedStatuses.includes(this.horse.horseStatus);
        },
        displayRejectButton() {
            if (this.systemRole === SystemRole.ADMIN || this.systemRole === SystemRole.THIRD_LEVEL_EDITOR) {
                if (this.horse.laboratoryTest.length > 1) {
                    if (this.horse.laboratoryTest.at(-1).laboratoryTestResult === "PARENTAGE_VERIFIED") {
                        return false;
                    }
                        const testResult = this.horse.laboratoryTest[this.horse.laboratoryTest.length - 2].laboratoryTestResult;
                        return testResult === "PARENTAGE_NOT_VERIFIED" || testResult === "NEEDS_RECOLLECTION";
                }
                return false;
            }
            return false;
        },
        computedRequired() {
            const allowedRoles = [
                SystemRole.FIRST_LEVEL_EDITOR,
                SystemRole.SECOND_LEVEL_EDITOR,
                SystemRole.ADMIN,
            ];

            return allowedRoles.includes(this.systemRole);
        },
        computedSelectedCoveringCertificateId() {
            return this?.horse.selectedCoveringCertificate?.id;
        },
    },
    watch: {
        "horse.breeders": {
            handler(newBreeders) {
                if (this.sameAsBreeders) {
                    this.horse.owners = newBreeders.map((breeder) => breeder);
                }
            },
            deep: true, // Enables array tracking
            immediate: true,
        },
        "horse.selectedCoveringCertificate": function (newCertificate) {
            if ((this.horse.horseStatus == null || this.horse?.horseStatus === HorseStatus.NEW_FOAL) && newCertificate != null) {
                this.horse.sireFullNameWithCountryCode = newCertificate.sireFullNameWithCountryCode;
                this.horse.damFullNameWithCountryCode = newCertificate.damFullNameWithCountryCode;
                this.horse.sireColorName = newCertificate.sireColorName;
                this.horse.damColorName = newCertificate.damColorName;
                this.horse.lastCoveringDate = newCertificate.lastCoveringDate;
                this.shouldValidateColor = true;
            }
        },
    },
};
</script>

<style lang="scss">
.foal-form {
    .separator {
        width: 100%;
        height: 1.5px;
        background-color: var(--separator-color);
    }

    .foal-covering-certificate {
        .custom-input-text, .custom-autocomplete {
            padding-left: 250px;
            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 240px;
                text-align: right;
            }
        }
        @media screen and (max-width: 1300px) {
            justify-content: center;
            gap: 4rem !important;
            .custom-input-text, .custom-autocomplete {
                padding-left: 170px;
                .custom-label {
                    left: 160px;
                }
            }
        }
        @media screen and (max-width: 1000px) {
            flex-direction: column;
            align-items: center;
            gap: 1rem !important;
            .custom-input-text, .custom-autocomplete {
                padding-left: 0;
                .custom-label {
                    left: -10px;
                    max-width: 200px;
                }
            }
        }
        @media screen and (max-width: 700px) {
            align-items: end;
            .foal-covering-certificate-inner {
                width: 60% !important;
            }
        }
    }
}
//forgive me this bs solution
.hidden-reject-button {
    display: none !important;
}
.p-confirm-dialog-reject {
    display: none !important;
}
</style>
